import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import ThemeConstants from '../../../../config/theme/theme_constants';
import UserTitle from '../components/user_title';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { UserInterface } from '../../../../core/components/user/domain/interfaces/user_interface';
import { useGetResourceByThemeId } from '../../domain/hooks/use_resources_by_theme_id';
import ResourceCategory from '../components/resource/resource_category';
import Navbar from '../../../../core/components/navbar/navbar';
import { useGetFavoritesByUserIdAndThemeId } from '../../domain/hooks/use_get_favorites_by_user_id_and_theme_id';
import PointIcon from '../../../../assets/images/uvibes_point.svg?react';
import AreaOfDevelopment from '../components/area_of_development';
import BackArrowBlackIcon from '../../../../assets/images/arrow_back_black_icon.svg?react';
import { useGetThemeById } from '../../domain/hooks/use_theme';
import { SyncLoader } from 'react-spinners';
import { CareerStatusEnum } from '../../../../core/enum/career_status_enum';
import { getCurrentUserTheme } from '../../domain/utils/get_current_user_theme';
import { Trans } from 'react-i18next';
import { useTranslation } from '../../../../i18n/useTranslation';
import PartnersSection from '../../../../core/components/partners_section';

const ThemePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const navigate = useNavigate();
  const { themeId } = useParams();
  const { course_theme, experience, userId, company, careerStatus } = useOutletContext<UserInterface>();

  useEffect(() => {
    if (company?.permissions.course === true && careerStatus === CareerStatusEnum.Start) {
      navigate('/career/start', { replace: true });
    }
  }, [careerStatus, company?.permissions.course, navigate]);
  const { data: themeData, error: themeError, refetch, isLoading: isGettingTheme } = useGetThemeById(themeId);
  const { data: resources, videos, audios, articles, hasMoreVideos, hasMoreArticles, hasMoreAudios, isPending: isGettingResources } = useGetResourceByThemeId(themeData?._id);
  const { favorites, hasMoreFavorites } = useGetFavoritesByUserIdAndThemeId(userId, themeData?._id,);
  const hasViewedAllResources = useMemo(() => {
    if (!resources) return false;
    return resources.every(resource => resource.viewed);
  }, [resources]);

  const requiredExp = useMemo(() => {
    return company?.limits?.pointsFinishTheme ?? 200;
  }, [company?.limits?.pointsFinishTheme]);

  useEffect(() => {
    const currentTheme = getCurrentUserTheme(course_theme, company?.id);
    if (!currentTheme && company?.permissions.ressource === false) {
      navigate('/career/start', { replace: true });
    } else if (currentTheme && currentTheme._themeId !== themeId) {
      const themeId = typeof currentTheme._themeId === 'string' ? currentTheme._themeId : currentTheme._themeId!._id;
      navigate(`/career/${themeId}/resources`, { replace: true });
    }
  }, [course_theme, navigate, company?.id, company?.permissions.ressource, themeId]);

  useEffect(() => {
    if (themeError) {
      const data = themeError.response?.data as { message?: string } | undefined;
      const errorMessage = data?.message || 'An error occurred';
      if (errorMessage === "id contains invalid characters" || errorMessage === "Theme not found") {
        toast.error(t('course.theme_not_found'));
        navigate('/career/start', { replace: true });
      }
    }
  }, [themeError, navigate, t]);

  useEffect(() => {
    if (themeData === undefined && themeId === undefined) {
      navigate('/career/start', { replace: true });
    }
  }, [themeData, themeId, navigate]);

  const pointsLeft = Math.max(requiredExp - experience.courseExp, 0);

  const isLoading = isGettingTheme || isGettingResources;

  const barColor = themeData?.color ?? theme.UvibesPink;
  const targetProgress = experience.courseExp > requiredExp
    ? 100
    : (experience.courseExp / requiredExp) * 100;
  // Start full, then settle to actual on mount → bar "discharges" down to current level.
  const [progress, setProgress] = useState(100);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setProgress(targetProgress));
    return () => cancelAnimationFrame(frame);
  }, [targetProgress]);

  // Width of widest category row (capped to available space). All rows share it
  // so they start at the same left edge; the widest sits centered. Each row
  // scrolls on its own when its content exceeds this width.
  const stackRef = useRef<HTMLDivElement>(null);
  const [columnWidth, setColumnWidth] = useState<number>();

  useLayoutEffect(() => {
    const stack = stackRef.current;
    if (!stack) return;
    const measure = () => {
      const tracks = stack.querySelectorAll<HTMLElement>('[data-resource-track]');
      let widest = 0;
      tracks.forEach((track) => { widest = Math.max(widest, Math.ceil(track.getBoundingClientRect().width)); });
      const available = stack.parentElement?.clientWidth ?? widest;
      setColumnWidth(widest ? Math.min(widest, available) : undefined);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stack);
    if (stack.parentElement) observer.observe(stack.parentElement);
    return () => observer.disconnect();
  }, [favorites?.length, audios.length, articles.length, videos.length]);

  return (
    <div className="min-h-dvh pt-[5.6dvh] flex flex-col gap-6 items-center relative max-md:pb-32 md:pb-29">
      {company?.permissions.ressource === true && (
        <BackArrowBlackIcon className="absolute top-8 start-5 cursor-pointer" width={20} height={20} onClick={() => navigate(-1)}/>
      )}
      <div className='flex flex-col gap-6 items-center max-lg:px-[3%] lg:px-[1%] w-full'>

        <UserTitle />
        {isLoading && (
          <div className="absolute top-0 left-0 w-full h-full flex justify-center flex-col items-center">
            <SyncLoader color={themeData?.color || theme.UvibesPink} size={24} speedMultiplier={0.5} />
          </div>
        )}
        {themeError && (
          <div className="absolute top-0 left-0 w-full h-full flex justify-center flex-col items-center gap-3">
            <span className="text-sm text-medium text-center">{t('common.error.occurred')}</span>
            <button
                className="px-4 py-2 rounded-[18px] text-white font-semibold text-sm cursor-pointer"
                style={{ background: theme.UvibesPink }}
                onClick={() => refetch()}
            >
                {t('common.button.retry')}
            </button>
          </div>
        )}
        {themeData &&
          <AreaOfDevelopment
          id={themeData._id}
          name={themeData.name}
          img={themeData.picture}
          backgroundColor={themeData.color}
          mode="current"
          currentLabel={company?.permissions.course === true ? t('course.area.current_course') : undefined}
          />
        }
        
        {!isLoading && company?.permissions.course === true && <>
          {pointsLeft === 0
            ? <p className='text-[1.25rem] font-bold leading-none text-center'>
                <Trans
                  i18nKey="course.theme.completed"
                  values={{ points: requiredExp }}
                  components={{ light: <span className='font-normal' /> }}
                />
              </p>
            : <p className="text-xs pb-3.5">{t('course.theme.discover')}</p>
          }
          <div className="max-md:w-full flex flex-col items-center gap-2.5 md:w-3/4 max-w-200">
            <div className="flex justify-between items-center w-full">
              <h2 className="font-bold leading-tight">{t('course.theme.certificate_progress')}</h2>
              {pointsLeft !== 0 && <p className="text-xs">
                {t('course.theme.points_left', { points: pointsLeft })}
              </p>}
            </div>
            <div
              className="h-5 border-none rounded-full w-full overflow-hidden"
              style={{ backgroundColor: `color-mix(in srgb, ${barColor} 18%, transparent)` }}
            >
              <div
                className="h-full rounded-full transition-[width] duration-700 ease-out"
                style={{
                  width: `${progress}%`,
                  backgroundColor: barColor,
                }}
              />
            </div>
         
            {experience.courseExp >= requiredExp && hasViewedAllResources ? (
              <button
                className="text-white px-6 py-2.25 cursor-pointer rounded-full mt-5 mb-1.5 leading-thight font-bold"
                style={{ backgroundColor: themeData?.color ?? theme.UvibesPink }}
                onClick={() => navigate(`/career/${themeId}/end-quizz`)}
              >
                {t('course.theme.fill_evaluation')}
              </button>
            ) : (
              <>
                <p className="font-bold flex gap-2 items-center leading-snug">
                  <PointIcon className="w-auto h-4.5"/>
                  {t('course.theme.goal', { points: requiredExp })}
                </p>
                <p className="text-xs leading-relaxed px-6.5">
                  {t('course.theme.goal_hint', { points: requiredExp })}
                </p>
              </>
            )}
          </div>
        </>}
      </div>
      {!isLoading && (
      <div
        ref={stackRef}
        className="flex flex-col gap-10 mx-auto max-w-full"
        style={{ width: columnWidth ? `${columnWidth}px` : 'max-content' }}
      >
        {favorites && favorites.length > 0 && (
          <ResourceCategory
            title={t('course.resource.favorites')}
            color={theme.UvibesBlue}
            resources={favorites}
            path="favorite"
            showMore={hasMoreFavorites}
          />
        )}
        {audios.length > 0 && (
          <ResourceCategory
            title={t('course.resource.audios')}
            color={theme.UvibesOrange}
            resources={audios}
            path="audio"
            showMore={hasMoreAudios}
          />
        )}
        {articles.length > 0 && (
          <ResourceCategory
            title={t('course.resource.articles')}
            color={theme.UvibesGreen}
            resources={articles}
            path="article"
            showMore={hasMoreArticles}
          />
        )}
        {videos.length > 0 && (
          <ResourceCategory
            title={t('course.resource.videos')}
            color={theme.UvibesPink}
            resources={videos}
            path="video"
            showMore={hasMoreVideos}
          />
        )}
      </div>
      )}

      {!isLoading && <PartnersSection className="mt-4 px-3"/>}

      {!isLoading && (
      <div className="fixed flex justify-center bottom-6 w-full px-3 z-50">
        <Navbar />
      </div>
      )}
    </div>
  );
};

export default ThemePage;
